import React, { useEffect, useState } from "react";
import Resources from "../../../types/Resources";
import { DepartmentRoleOptions } from "../../../types/DepartmentRoleOptions";
import {
  UserFormState_fromUser,
  UserFormState_toUserRequest,
} from "../../../types/forms/UserFormState";
import { getDetailedUserInfoAsync, updateUserAsync } from "../../../apiClient";
import {
  showDangerAlert,
  showDangerDialog,
  showSuccessAlert,
} from "../../../common";
import useUserForm from "../useUserForm";

export type EditUserFormProps = {
  userId: string;
  resources: Resources;
  departmentRoleOptions: DepartmentRoleOptions[];
};

function useEditUserForm(props: EditUserFormProps) {
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(true);
  const [canEditPassword, setCanEditPassword] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const baseFormContent = useUserForm({
    resources: props.resources,
    showNewUserRoleRowDefault: false,
  });

  useEffect(() => {
    async function fetchInitialDataAsync() {
      const userInfoResult = await getDetailedUserInfoAsync(props.userId);

      if (userInfoResult.failed) {
        setIsLoadingUserInfo(false);
        showDangerDialog(
          props.resources.get("SomethingWentWrong"),
          props.resources.get("RefreshPageAndTryAgain"),
        );
        return;
      }

      const userInfo = userInfoResult.value;

      setCanEditPassword(userInfo.canEditPassword);
      baseFormContent.setFormState(
        UserFormState_fromUser(userInfo, userInfo.roles),
      );
      setIsLoadingUserInfo(false);
    }

    fetchInitialDataAsync();
  }, [props.userId]);

  async function handleSubmitAsync(e: React.FormEvent) {
    e.preventDefault();

    const request = UserFormState_toUserRequest(baseFormContent.formState);
    const result = await updateUserAsync(props.userId, request);

    if (result.failed) {
      const errors = result.error.errors;
      const hasMappedErrors = Object.keys(baseFormContent.formState).some(
        (key) => {
          const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
          const numberOfErrors = errors?.[pascalKey]?.length ?? 0;
          return numberOfErrors > 0;
        },
      );

      if (hasMappedErrors) {
        baseFormContent.setFormState((prev) => ({
          ...prev,
          firstName: {
            ...prev.firstName,
            errorMessage: errors["FirstName"]?.[0],
          },
          lastName: { ...prev.lastName, errorMessage: errors["LastName"]?.[0] },
          email: { ...prev.email, errorMessage: errors["Email"]?.[0] },
          phoneNumber: {
            ...prev.phoneNumber,
            errorMessage: errors["PhoneNumber"]?.[0],
          },
          companyId: {
            ...prev.companyId,
            errorMessage: errors["CompanyId"]?.[0],
          },
          newCompanyName: {
            ...prev.newCompanyName,
            errorMessage: errors["NewCompanyName"]?.[0],
          },
          roles: { ...prev.roles, errorMessage: errors["Roles"]?.[0] },
        }));
      } else {
        showDangerAlert(
          props.resources.get("EditUserError") ?? result.error.title,
        );
      }
      return;
    }

    showSuccessAlert(props.resources.get("UserEdited"));
  }

  function handlePasswordUpdated() {
    setShowChangePasswordModal(false);
    showSuccessAlert(props.resources.get("PasswordUpdated"));
  }

  return {
    ...baseFormContent,
    isLoading: isLoadingUserInfo || baseFormContent.isLoadingCompanyOptions,
    canEditPassword,
    showChangePasswordModal,
    handleSubmitAsync,
    handlePasswordUpdated,
    handleShowChangePasswordModal: () => setShowChangePasswordModal(true),
    handleHideChangePasswordModal: () => setShowChangePasswordModal(false),
  };
}

export default useEditUserForm;
